import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../components/layout/AdminLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Checkbox } from '../../components/ui/checkbox';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Bell,
  BellOff,
  Send,
  TrendingUp,
  Search,
  Plus,
  MoreHorizontal,
  Eye,
  Archive,
  ArchiveRestore,
  Trash2,
  CheckCircle,
  Circle,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Loader2,
  Users,
  User,
  Shield,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { adminNotificationAPI } from '../../services/api';
import { useSocket } from '../../context/SocketContext';
import CreateNotificationDialog from '../../components/admin/CreateNotificationDialog';
import ViewNotificationDialog from '../../components/admin/ViewNotificationDialog';
import DeleteNotificationDialog from '../../components/admin/DeleteNotificationDialog';

const PRIORITY_STYLES = {
  LOW: { bg: 'bg-stone-100', text: 'text-gray-700', label: 'Low' },
  NORMAL: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Normal' },
  HIGH: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'High' },
  URGENT: { bg: 'bg-red-100', text: 'text-red-700', label: 'Urgent' },
};

const TYPE_STYLES = {
  GENERAL: { bg: 'bg-[#F2575F]/10', text: 'text-[#F2575F]', label: 'General' },
  SYSTEM: { bg: 'bg-slate-100', text: 'text-slate-700', label: 'System' },
};

const TARGET_TYPE_ICONS = {
  ALL: Users,
  ROLE: Shield,
  USER: User,
};

export default function AdminNotifications() {
  // State
  const [notifications, setNotifications] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    unread: 0,
    sentToday: 0,
    sentThisWeek: 0,
  });
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalPages: 1,
    total: 0,
  });

  // Filters
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showArchived, setShowArchived] = useState(false);

  // Selection
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  // Dialogs
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [bulkDeleteMode, setBulkDeleteMode] = useState(false);

  // Socket
  const { socket } = useSocket();

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchDebounced(search);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        isArchived: showArchived.toString(),
      };

      if (searchDebounced) params.search = searchDebounced;
      if (typeFilter && typeFilter !== 'all') params.type = typeFilter;
      if (priorityFilter && priorityFilter !== 'all') params.priority = priorityFilter;
      if (statusFilter === 'read') params.isRead = 'true';
      if (statusFilter === 'unread') params.isRead = 'false';

      const response = await adminNotificationAPI.getAll(params);
      // Handle both response structures: { data: { notifications, pagination } } or { notifications, pagination }
      const data = response.data || response;
      setNotifications(data.notifications || []);
      setPagination((prev) => ({
        ...prev,
        totalPages: data.pagination?.totalPages || 1,
        total: data.pagination?.total || 0,
      }));
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
      setError('Failed to load notifications');
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, searchDebounced, typeFilter, priorityFilter, statusFilter, showArchived]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const response = await adminNotificationAPI.getStats();
      // Handle both response structures
      const data = response.data || response;
      setStats(data || {
        total: 0,
        unread: 0,
        sentToday: 0,
        sentThisWeek: 0,
      });
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchNotifications();
    fetchStats();
  }, [fetchNotifications, fetchStats]);

  // Socket listeners for real-time updates
  useEffect(() => {
    if (!socket) return;

    const handleAdminNotificationCreated = () => {
      fetchStats();
      if (pagination.page === 1) {
        fetchNotifications();
      }
      toast.info('New notification sent');
    };

    socket.on('admin_notification_created', handleAdminNotificationCreated);

    return () => {
      socket.off('admin_notification_created', handleAdminNotificationCreated);
    };
  }, [socket, fetchStats, fetchNotifications, pagination.page]);

  // Reset selection when data changes
  useEffect(() => {
    setSelectedIds([]);
    setSelectAll(false);
  }, [notifications]);

  // Handlers
  const handlePageChange = (newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    if (checked) {
      setSelectedIds(notifications.map((n) => n._id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id, checked) => {
    if (checked) {
      setSelectedIds((prev) => [...prev, id]);
    } else {
      setSelectedIds((prev) => prev.filter((i) => i !== id));
      setSelectAll(false);
    }
  };

  const handleArchive = async (notification) => {
    try {
      await adminNotificationAPI.archive(notification._id);
      toast.success('Notification archived');
      fetchNotifications();
      fetchStats();
    } catch (err) {
      toast.error('Failed to archive notification');
    }
  };

  const handleUnarchive = async (notification) => {
    try {
      await adminNotificationAPI.unarchive(notification._id);
      toast.success('Notification restored');
      fetchNotifications();
      fetchStats();
    } catch (err) {
      toast.error('Failed to restore notification');
    }
  };

  const handleBulkArchive = async () => {
    if (selectedIds.length === 0) return;
    try {
      await adminNotificationAPI.bulkAction(selectedIds, showArchived ? 'unarchive' : 'archive');
      toast.success(`${selectedIds.length} notification(s) ${showArchived ? 'restored' : 'archived'}`);
      fetchNotifications();
      fetchStats();
    } catch (err) {
      toast.error('Failed to perform bulk action');
    }
  };

  const handleView = (notification) => {
    setSelectedNotification(notification);
    setViewDialogOpen(true);
  };

  const handleDelete = (notification) => {
    setSelectedNotification(notification);
    setBulkDeleteMode(false);
    setDeleteDialogOpen(true);
  };

  const handleBulkDelete = () => {
    if (selectedIds.length === 0) return;
    setBulkDeleteMode(true);
    setSelectedNotification(null);
    setDeleteDialogOpen(true);
  };

  const handleRefresh = () => {
    fetchNotifications();
    fetchStats();
  };

  const handleSuccess = () => {
    fetchNotifications();
    fetchStats();
    setSelectedIds([]);
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const formatTime = (date) => {
    if (!date) return '';
    try {
      return formatDistanceToNow(new Date(date), { addSuffix: true });
    } catch {
      return '';
    }
  };

  // Stats cards data
  const statsCards = [
    {
      title: 'Total Notifications',
      value: stats.total,
      icon: Bell,
      color: 'bg-blue-500',
      bgLight: 'bg-blue-50',
    },
    {
      title: 'Unread',
      value: stats.unread,
      icon: BellOff,
      color: 'bg-amber-500',
      bgLight: 'bg-amber-50',
    },
    {
      title: 'Sent Today',
      value: stats.sentToday,
      icon: Send,
      color: 'bg-green-500',
      bgLight: 'bg-green-50',
    },
    {
      title: 'Sent This Week',
      value: stats.sentThisWeek,
      icon: TrendingUp,
      color: 'bg-[#F2575F]',
      bgLight: 'bg-[#F2575F]/5',
    },
  ];

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-notifications-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-outfit font-bold text-gray-900">Notification Management</h1>
            <p className="text-gray-600">Send and manage notifications across the platform</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={handleRefresh} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button onClick={() => setCreateDialogOpen(true)} data-testid="create-notification-btn">
              <Plus className="w-4 h-4 mr-2" />
              Create Notification
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {statsCards.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <motion.div
                key={stat.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className={`p-4 ${stat.bgLight} border-none`}>
                  <div className="flex items-center gap-4">
                    <div className={`p-3 rounded-lg ${stat.color}`}>
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">{stat.title}</p>
                      <p className="text-2xl font-bold">
                        {statsLoading ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                          stat.value
                        )}
                      </p>
                    </div>
                  </div>
                </Card>
              </motion.div>
            );
          })}
        </div>

        {/* Filters & Actions Bar */}
        <Card className="p-4">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search notifications..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-2">
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-[130px]">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="GENERAL">General</SelectItem>
                  <SelectItem value="SYSTEM">System</SelectItem>
                </SelectContent>
              </Select>

              <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                <SelectTrigger className="w-[130px]">
                  <SelectValue placeholder="Priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Priorities</SelectItem>
                  <SelectItem value="LOW">Low</SelectItem>
                  <SelectItem value="NORMAL">Normal</SelectItem>
                  <SelectItem value="HIGH">High</SelectItem>
                  <SelectItem value="URGENT">Urgent</SelectItem>
                </SelectContent>
              </Select>

              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[130px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="read">Read</SelectItem>
                  <SelectItem value="unread">Unread</SelectItem>
                </SelectContent>
              </Select>

              <Button
                variant={showArchived ? 'default' : 'outline'}
                onClick={() => setShowArchived(!showArchived)}
                className="gap-2"
              >
                <Archive className="w-4 h-4" />
                {showArchived ? 'Show Active' : 'Show Archived'}
              </Button>
            </div>

            {/* Bulk Actions */}
            {selectedIds.length > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">
                  {selectedIds.length} selected
                </span>
                <Button variant="outline" size="sm" onClick={handleBulkArchive}>
                  {showArchived ? (
                    <>
                      <ArchiveRestore className="w-4 h-4 mr-1" />
                      Restore
                    </>
                  ) : (
                    <>
                      <Archive className="w-4 h-4 mr-1" />
                      Archive
                    </>
                  )}
                </Button>
                <Button variant="destructive" size="sm" onClick={handleBulkDelete}>
                  <Trash2 className="w-4 h-4 mr-1" />
                  Delete
                </Button>
              </div>
            )}
          </div>
        </Card>

        {/* Notifications Table */}
        <Card className="overflow-hidden">
          {error ? (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500">
              <Bell className="w-12 h-12 mb-4 text-gray-300" />
              <p className="text-lg font-medium">Failed to load notifications</p>
              <p className="text-sm mb-4">{error}</p>
              <Button variant="outline" onClick={handleRefresh}>
                Try Again
              </Button>
            </div>
          ) : loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500">
              <Bell className="w-12 h-12 mb-4 text-gray-300" />
              <p className="text-lg font-medium">No notifications found</p>
              <p className="text-sm">
                {showArchived
                  ? 'No archived notifications'
                  : 'Create a new notification to get started'}
              </p>
            </div>
          ) : (
            <>
              {/* Table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-stone-50 border-b">
                    <tr>
                      <th className="px-4 py-3 w-10">
                        <Checkbox
                          checked={selectAll}
                          onCheckedChange={handleSelectAll}
                        />
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">
                        Recipient
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">
                        Title
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">
                        Type
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">
                        Priority
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">
                        Status
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">
                        Sent
                      </th>
                      <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    <AnimatePresence>
                      {notifications.map((notification) => {
                        const priorityStyle =
                          PRIORITY_STYLES[notification.priority] || PRIORITY_STYLES.NORMAL;
                        const typeStyle = TYPE_STYLES[notification.type] || TYPE_STYLES.GENERAL;
                        const TargetIcon = TARGET_TYPE_ICONS[notification.targetType] || Users;

                        return (
                          <motion.tr
                            key={notification._id}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className={`hover:bg-stone-50/80 ${
                              !notification.isRead ? 'bg-blue-50/30' : ''
                            }`}
                          >
                            <td className="px-4 py-3">
                              <Checkbox
                                checked={selectedIds.includes(notification._id)}
                                onCheckedChange={(checked) =>
                                  handleSelectOne(notification._id, checked)
                                }
                              />
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                {notification.targetType === 'USER' ? (
                                  <Avatar className="h-8 w-8">
                                    <AvatarImage src={notification.recipientAvatar} />
                                    <AvatarFallback className="text-xs">
                                      {getInitials(notification.recipientName)}
                                    </AvatarFallback>
                                  </Avatar>
                                ) : (
                                  <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                                    <TargetIcon className="h-4 w-4 text-primary" />
                                  </div>
                                )}
                                <div>
                                  <p className="text-sm font-medium">
                                    {notification.targetType === 'ALL'
                                      ? 'All Users'
                                      : notification.targetType === 'ROLE'
                                      ? notification.targetRole
                                      : notification.recipientName || 'Unknown'}
                                  </p>
                                  <p className="text-xs text-muted-foreground">
                                    {notification.targetType}
                                  </p>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <p className="text-sm font-medium truncate max-w-[200px]">
                                {notification.title}
                              </p>
                            </td>
                            <td className="px-4 py-3">
                              <Badge className={`${typeStyle.bg} ${typeStyle.text}`}>
                                {typeStyle.label}
                              </Badge>
                            </td>
                            <td className="px-4 py-3">
                              <Badge className={`${priorityStyle.bg} ${priorityStyle.text}`}>
                                {priorityStyle.label}
                              </Badge>
                            </td>
                            <td className="px-4 py-3">
                              <Badge variant={notification.isRead ? 'outline' : 'default'}>
                                {notification.isRead ? (
                                  <>
                                    <CheckCircle className="h-3 w-3 mr-1" />
                                    Read
                                  </>
                                ) : (
                                  <>
                                    <Circle className="h-3 w-3 mr-1" />
                                    Unread
                                  </>
                                )}
                              </Badge>
                            </td>
                            <td className="px-4 py-3">
                              <p className="text-sm text-muted-foreground">
                                {formatTime(notification.createdAt)}
                              </p>
                            </td>
                            <td className="px-4 py-3 text-right">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="icon">
                                    <MoreHorizontal className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => handleView(notification)}>
                                    <Eye className="h-4 w-4 mr-2" />
                                    View Details
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  {notification.isArchived ? (
                                    <DropdownMenuItem onClick={() => handleUnarchive(notification)}>
                                      <ArchiveRestore className="h-4 w-4 mr-2" />
                                      Restore
                                    </DropdownMenuItem>
                                  ) : (
                                    <DropdownMenuItem onClick={() => handleArchive(notification)}>
                                      <Archive className="h-4 w-4 mr-2" />
                                      Archive
                                    </DropdownMenuItem>
                                  )}
                                  <DropdownMenuItem
                                    onClick={() => handleDelete(notification)}
                                    className="text-red-600"
                                  >
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    Delete
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </td>
                          </motion.tr>
                        );
                      })}
                    </AnimatePresence>
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between px-4 py-3 border-t bg-stone-50">
                <p className="text-sm text-muted-foreground">
                  Showing {(pagination.page - 1) * pagination.limit + 1} to{' '}
                  {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                  {pagination.total} notifications
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={pagination.page <= 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Previous
                  </Button>
                  <div className="flex items-center gap-1">
                    {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                      let pageNum;
                      if (pagination.totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (pagination.page <= 3) {
                        pageNum = i + 1;
                      } else if (pagination.page >= pagination.totalPages - 2) {
                        pageNum = pagination.totalPages - 4 + i;
                      } else {
                        pageNum = pagination.page - 2 + i;
                      }
                      return (
                        <Button
                          key={pageNum}
                          variant={pagination.page === pageNum ? 'default' : 'outline'}
                          size="sm"
                          className="w-9"
                          onClick={() => handlePageChange(pageNum)}
                        >
                          {pageNum}
                        </Button>
                      );
                    })}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={pagination.page >= pagination.totalPages}
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </Card>

        {/* Dialogs */}
        <CreateNotificationDialog
          open={createDialogOpen}
          onOpenChange={setCreateDialogOpen}
          onSuccess={handleSuccess}
        />

        <ViewNotificationDialog
          open={viewDialogOpen}
          onOpenChange={setViewDialogOpen}
          notification={selectedNotification}
        />

        <DeleteNotificationDialog
          open={deleteDialogOpen}
          onOpenChange={setDeleteDialogOpen}
          notification={selectedNotification}
          bulkMode={bulkDeleteMode}
          selectedIds={selectedIds}
          onSuccess={handleSuccess}
        />
      </div>
    </AdminLayout>
  );
}
